import type { ReactNode, RefObject } from 'react';

import { Flex } from '@kognitos/lattice';

/**
 * Props for {@link AssetWorkspaceLayout}.
 */
export interface IAssetWorkspaceLayoutProps {
  ref?: RefObject<HTMLDivElement | null>;
  /** Which builder this workspace belongs to, e.g. `automations`; set as `data-asset-type`. */
  kind?: string;
  /**
   * The view shows a draft: selections, their hovers and the active document
   * tab take the Draft badge's blue. Set as `data-asset-draft`.
   */
  isDraft?: boolean;
  /** The header, normally an `AssetWorkspaceHeader`. Spans the full width. */
  header?: ReactNode;
  /** Full-width notices between the header and the view, e.g. support access. */
  banner?: ReactNode;
  /** The view: an `AssetDefinitionLayout` or an `AssetActivityLayout`. */
  children: ReactNode;
  /** Dialogs and sheets owned by the page. */
  overlays?: ReactNode;
  'data-testid'?: string;
}

/**
 * The full-height frame of an asset's workspace, shared by automations, agents
 * and context graphs: the header across the top, the same on Definition and
 * Activity, then optional banners and the view filling the rest of the
 * screen. It is the only element in the workspace that owns the viewport
 * height.
 */
export function AssetWorkspaceLayout({
  ref,
  kind,
  isDraft,
  header,
  banner,
  children,
  overlays,
  'data-testid': dataTestId = 'asset-workspace',
}: IAssetWorkspaceLayoutProps) {
  return (
    <Flex
      ref={ref}
      direction="column"
      width="full"
      className="h-screen min-h-0"
      data-asset-type={kind}
      data-asset-draft={isDraft || undefined}
      data-testid={dataTestId}
    >
      {header}
      <Flex
        direction="column"
        gap={4}
        paddingX={4}
        width="full"
        className="min-h-0 flex-1 pt-4"
      >
        {banner}
        {children}
      </Flex>
      {overlays}
    </Flex>
  );
}
